import { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Flex, Button } from 'antd'
import { Table, Text } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AIChatFallbackContactFragment } from 'types/graphql'

import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useGetAIChatFallbackContactsQuery } from 'src/fetch/aiChatFallbackContacts'
import { useDisclosure } from 'src/hooks/use-disclosure'
import AIChatFallbackContactButtons from 'src/pages/AIAssistantRoutingPage/AIChatFallbackContactButtons'
import AIChatFallbackContactModal from 'src/pages/AIAssistantRoutingPage/AIChatFallbackContactModal'
import { formatUserName } from 'src/utils'

export const isDefaultContact = (contact: AIChatFallbackContactFragment) =>
  typeof contact.fallbackConditions !== 'string'

const RoutingTable = () => {
  const tableProps = useTableState<AIChatFallbackContactFragment>()
  const { aiChatFallbackContacts, isLoading, refetch } = useGetAIChatFallbackContactsQuery()
  const createModalState = useDisclosure()

  const anyContactHasUsageCount = useMemo(
    () => aiChatFallbackContacts.some((c) => c.usageCount ?? 0 > 0),
    [aiChatFallbackContacts]
  )

  const sortedContacts = useMemo(() => {
    return aiChatFallbackContacts
      .filter((c) => !isDefaultContact(c))
      .sort((a, b) => (a.fallbackConditions || '').localeCompare(b.fallbackConditions || ''))
  }, [aiChatFallbackContacts])

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AIChatFallbackContactFragment>()
    return [
      columnHelper.display({
        id: 'fallbackConditions',
        header: 'Context',
        cell: (info) => info.row.original.fallbackConditions,
      }),
      columnHelper.display({
        id: 'contact',
        header: 'Contact',
        cell: (info) => {
          return info.row.original.user ? (
            <Flex align="center" gap="6px">
              <UserAvatar sizePx={20} user={info.row.original.user} />
              <Text>{formatUserName(info.row.original.user)}</Text>
            </Flex>
          ) : (
            info.row.original.contactString || '-'
          )
        },
      }),
      ...(anyContactHasUsageCount
        ? [
            columnHelper.accessor('usageCount', {
              id: 'usageCount',
              header: 'Usage Count',
              cell: (info) => info.row.original.usageCount ?? '',
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <AIChatFallbackContactButtons
            aiChatFallbackContact={info.row.original}
            refetch={refetch}
          />
        ),
      }),
    ]
  }, [refetch, anyContactHasUsageCount])

  const getRowId = useCallback(
    (aiChatFallbackContact: AIChatFallbackContactFragment) => `${aiChatFallbackContact.id}`,
    []
  )

  return (
    <Flex vertical gap="16px" align="flex-end">
      <Button type="primary" onClick={createModalState.open}>
        Create fallback contact
      </Button>
      <Table
        columns={columns}
        data={sortedContacts}
        enablePagination={false}
        getRowId={getRowId}
        isLoading={isLoading}
        {...tableProps}
      />
      {createModalState.isOpen && (
        <AIChatFallbackContactModal onClose={createModalState.close} refetch={refetch} />
      )}
    </Flex>
  )
}

export default React.memo(RoutingTable)
